import api from './api';
import { 
  FinancialInstitutionResponse, 
  CreateFinancialInstitutionRequest,
  UpdateFinancialInstitutionRequest,
  FinancialInstitutionFilters 
} from '../types/financialInstitution';
import { ApiResponse } from '../types/api';
import { sanitizeFormData, detectSqlInjection, toISO8601Date, sanitizeUrl } from '../utils/sanitize';

/**
 * Transforme les données d'institution financière pour le backend
 * - Convertit les dates en ISO 8601
 * - Supprime les champs vides (les convertit en undefined pour ne pas les envoyer)
 * - Assure que les strings obligatoires ne sont pas null
 * - Convertit les enums en format backend (UPPER_CASE)
 */
/**
 * 'create' : corps complet, `userId` exigé, champs requis absents complétés.
 * 'update' : PATCH partiel. Ni `userId` (le formulaire ne l'envoie pas, et
 * l'exiger levait une erreur AVANT tout appel réseau : « Sauvegarder » ne
 * faisait rien), ni champ ajouté (un envoi du seul logo effacerait sinon
 * l'agrément, le RCCM et le NIF).
 */
type InstitutionPayloadMode = 'create' | 'update';

const transformInstitutionDataForBackend = <T extends object>(data: T, mode: InstitutionPayloadMode): T => {
  // Copie indexable en écriture : on ajoute, convertit et retire des clés.
  const transformed: Record<string, unknown> = { ...(data as Record<string, unknown>) };

  console.log('🔍 Données avant transformation:', JSON.stringify(data, null, 2));

  // Liste des champs de date à transformer en ISO 8601
  // Ces champs sont REQUIS par le backend - on doit envoyer une date valide
  const dateFields = ['dateCreation', 'dateAgrement', 'validiteAgrement', 'dateDebutActivites'];
  
  for (const field of dateFields) {
    if (field in transformed) {
      const value = transformed[field];
      console.log(`📅 Champ date "${field}":`, value, typeof value);
      
      if (value && value !== '') {
        const isoDate = toISO8601Date(value as string | Date | number);
        console.log(`📅 Conversion de "${field}":`, value, '→', isoDate);
        
        if (isoDate) {
          transformed[field] = isoDate;
        } else {
          // Si la conversion échoue, supprimer le champ (le backend décidera si c'est requis)
          console.warn(`⚠️ Conversion échouée pour "${field}", suppression du champ`);
          delete transformed[field];
        }
      } else {
        // Si vide, supprimer le champ - le backend validera si c'est requis
        delete transformed[field];
      }
    }
  }

  // Liste des champs string REQUIS par le backend
  // Si vides, on les garde comme strings vides "" au lieu de les supprimer
  // Programme d'appui seul : ni agrément, ni RCCM, ni NIF exigés ; une chaîne
  // vide n'est jamais envoyée (le champ est simplement absent).
  const isProgramOnly = transformed.variant === 'support_program';
  const requiredStringFields = isProgramOnly
    ? ['sigle']
    : ['numeroAgrement', 'numeroRCCM', 'numeroNIF', 'sigle'];
  if (isProgramOnly) {
    for (const field of ['numeroAgrement', 'numeroRCCM', 'numeroNIF']) {
      const value = transformed[field];
      if (value === '' || value === null || value === undefined) delete transformed[field];
    }
  }

  for (const field of requiredStringFields) {
    if (field in transformed) {
      const value = transformed[field];
      console.log(`📝 Champ string requis "${field}":`, value, typeof value);
      
      // Toujours convertir en string (même vide)
      if (value === null || value === undefined) {
        transformed[field] = '';
      } else if (typeof value !== 'string') {
        transformed[field] = String(value);
      }
      // Si c'est déjà une string (même vide ''), on la garde telle quelle
    } else if (mode === 'create') {
      // Si le champ n'existe pas, l'ajouter comme string vide
      transformed[field] = '';
    }
  }

  // ✨ IMPORTANT: à la création, userId est OBLIGATOIRE et ne doit JAMAIS être vide
  // Le backend rejette les valeurs null ou string vide pour userId
  const userIdValue = transformed['userId'];
  console.log(`👤 userId dans transformation:`, userIdValue, typeof userIdValue);

  if (mode === 'create' && (!userIdValue || userIdValue === '')) {
    console.error('❌ userId manquant ou vide dans les données de transformation');
    console.error('❌ Données reçues:', JSON.stringify(data, null, 2));
    throw new Error('Profil utilisateur incomplet pour créer l\'institution. Veuillez rafraîchir la page.');
  }

  // S'assurer que c'est une string
  if (userIdValue && typeof userIdValue !== 'string') {
    transformed['userId'] = String(userIdValue);
  }

  // Liste des champs string OPTIONNELS - si vides, on les supprime
  const optionalStringFields = ['sousCategorie', 'codeSuccursale', 'institutionMereId'];
  
  for (const field of optionalStringFields) {
    if (field in transformed) {
      const value = transformed[field];
      console.log(`📝 Champ string optionnel "${field}":`, value, typeof value);
      
      if (value === '' || value === null || value === undefined) {
        delete transformed[field];
      } else if (typeof value !== 'string') {
        transformed[field] = String(value);
      }
    }
  }

  // ✨ ÉTAPE IMPORTANTE: Convertir les noms de champs frontend → backend
  // Le frontend utilise des accents (autoritéSupervision), le backend non (autoriteSupervision)
  const fieldMappings: Record<string, string> = {
    'autoritéSupervision': 'autoriteSupervision',
    // Ajouter d'autres mappings si nécessaire
  };
  
  for (const [frontendField, backendField] of Object.entries(fieldMappings)) {
    if (frontendField in transformed) {
      transformed[backendField] = transformed[frontendField];
      delete transformed[frontendField];
      console.log(`🔄 Renommage pour backend: ${frontendField} → ${backendField}`);
    }
  }

  // Liste des champs ENUM - si vides, on les supprime (le backend n'accepte que des valeurs valides)
  // ✨ IMPORTANT: Les valeurs restent en minuscules selon les enums backend
  // ✨ NOTE: Utiliser les noms backend (sans accents) car le mapping a été fait ci-dessus
  const enumFields = [
    'typeInstitution',            // un type vide ferait échouer @IsEnum côté serveur
    'typeStructure',              // filiale, mere, independante, siege_social
    'statutJuridique',            // sa, sarl, cooperative, mutual, foundation, branch, other
    'autoriteSupervision',        // bcc, arca, asmf, other (nom backend, sans accent)
    'segmentClientelePrincipal',  // individuals, sme, large_enterprises, institutions, all_segments
    'motivationPrincipale',       // new_clients, portfolio_diversification, etc.
  ];
  
  for (const field of enumFields) {
    if (field in transformed) {
      const value = transformed[field];
      console.log(`🔤 Champ enum "${field}":`, value, typeof value);
      
      if (value === '' || value === null || value === undefined) {
        console.log(`🗑️ Suppression du champ enum vide "${field}"`);
        delete transformed[field];
      }
      // Les valeurs restent telles quelles (minuscules)
    }
  }

  // Supprimer les autres champs vides/undefined (sauf ceux requis traités ci-dessus)
  // ✨ IMPORTANT: Ne JAMAIS supprimer userId - il est obligatoire pour la création
  // ✨ ANTI-PERTE DE DONNÉES: Ne pas supprimer les tableaux vides - ils représentent une intention explicite
  const protectedFields = [...requiredStringFields, 'userId'];
  
  // ✨ VALIDER LES CHAMPS URL (le backend attend des URLs valides avec @IsUrl())
  // Ces champs doivent contenir des URLs Cloudinary ou autres URLs valides
  const urlFields = ['logo', 'ceoPhoto', 'siteWeb'];
  for (const field of urlFields) {
    if (field in transformed && transformed[field]) {
      const rawUrl = transformed[field];
      // sanitizeUrl renvoie '' pour toute valeur non-string : même résultat sans la lui passer
      const validUrl = typeof rawUrl === 'string' ? sanitizeUrl(rawUrl) : '';
      if (validUrl) {
        transformed[field] = validUrl;
      } else {
        // URL invalide → supprimer le champ (optionnel dans le DTO)
        console.log(`🔗 URL invalide pour "${field}", suppression du champ`);
        delete transformed[field];
      }
    }
  }
  
  for (const key in transformed) {
    // Ne pas supprimer les champs protégés (requis + userId)
    if (protectedFields.includes(key)) continue;
    
    const value = transformed[key];
    
    // ✨ Conserver les tableaux (même vides) - ce sont des données valides
    if (Array.isArray(value)) {
      continue;
    }
    
    if (value === undefined) {
      delete transformed[key];
    }
    // Garder null seulement si explicitement voulu
    if (value === null && !['institutionMere'].includes(key)) {
      delete transformed[key];
    }
  }

  console.log('✅ Données après transformation:', JSON.stringify(transformed, null, 2));

  return transformed as T;
};

/**
 * Sanitise et transforme les données d'une institution financière avant envoi
 * - Détecte les injections SQL
 * - Sanitise les champs texte
 * - Transforme les dates en ISO 8601
 * - Supprime les champs vides pour éviter les erreurs de validation
 */
const sanitizeInstitutionData = <T extends object>(data: T, mode: InstitutionPayloadMode): T => {
  console.log('🔍 Données brutes reçues:', JSON.stringify(data, null, 2));

  // ✨ ÉTAPE 1: Transformation et nettoyage AVANT sanitisation
  // Ceci permet de supprimer les champs vides et transformer les dates
  // avant que sanitizeFormData ne puisse les modifier
  const transformed = transformInstitutionDataForBackend(data, mode);
  
  console.log('🔄 Données après transformation:', JSON.stringify(transformed, null, 2));

  // ✨ ÉTAPE 2: Vérification des tentatives d'injection SQL
  const jsonString = JSON.stringify(transformed);
  if (detectSqlInjection(jsonString)) {
    console.warn('⚠️ Tentative d\'injection SQL détectée dans les données institution');
    throw new Error('Données invalides détectées. Veuillez vérifier vos entrées.');
  }

  // ✨ ÉTAPE 3: Sanitisation des champs texte (sur les données déjà nettoyées)
  const sanitized = sanitizeFormData(transformed, {
    denominationSociale: { maxLength: 200 },
    sigle: { maxLength: 50 },
    description: { maxLength: 5000 },
    email: { toLowerCase: true },
    numeroAgrement: { maxLength: 100 },
    numeroRCCM: { maxLength: 100 },
    numeroNIF: { maxLength: 100 },
    // Seuls quelques champs ont des options : les autres prennent les options par défaut.
  } as Parameters<typeof sanitizeFormData<T>>[1]);
  
  console.log('🔧 Données finales pour le backend:', JSON.stringify(sanitized, null, 2));

  return sanitized;
};

/**
 * ✨ Transforme les données du backend vers le format frontend
 * Le backend utilise des noms sans accents, le frontend avec accents
 * Mapping: autoriteSupervision → autoritéSupervision
 */
const transformBackendToFrontend = <T extends object>(data: T): T => {
  if (!data) return data;
  
  const transformed: Record<string, unknown> = { ...(data as Record<string, unknown>) };
  
  // Mapping des champs backend (sans accents) vers frontend (avec accents)
  const fieldMappings: Record<string, string> = {
    'autoriteSupervision': 'autoritéSupervision',
    // Ajouter d'autres mappings si nécessaire
  };
  
  for (const [backendField, frontendField] of Object.entries(fieldMappings)) {
    if (backendField in transformed && !(frontendField in transformed)) {
      transformed[frontendField] = transformed[backendField];
      delete transformed[backendField];
      console.log(`🔄 Mapping backend→frontend: ${backendField} → ${frontendField}`);
    }
  }
  
  return transformed as T;
};

/**
 * Service pour la gestion des institutions financières
 * ✨ SIMPLIFIÉ: Transmet TOUTES les données sans transformation
 * 🔒 SÉCURISÉ: Sanitisation des entrées contre les injections
 * Les formulaires sont la source de vérité
 */
/**
 * Une agence telle que la route `financial-institutions/:id/branches` la
 * connait : un nom, un code unique chez l'institution, un type, un statut et
 * une adresse structuree. C'est ce decoupage qui permet a une COOPEC de
 * declarer ou elle opere reellement.
 */
export interface InstitutionBranchInput {
  name: string;
  code: string;
  type: string;
  status: string;
  address: {
    street?: string;
    city: string;
    province: string;
    postalCode?: string;
    country?: string;
  };
  contact?: { phone?: string; email?: string; fax?: string };
  manager?: { name?: string; phone?: string; email?: string };
  staffCount?: number;
  openingDate?: string;
  notes?: string;
}

export interface InstitutionBranch extends InstitutionBranchInput {
  id: string;
  institutionId: string;
  createdAt?: string;
  updatedAt?: string;
}

/** Programme d'appui proposé au choix d'une AVEC (GET /financial-institutions/support-programs). */
export interface SupportProgramOption {
  id: string;
  name: string;
  sigle?: string;
  /** Logo du programme, s'il en a un (affiché à côté de son nom). */
  logoUrl?: string | null;
  province: string | null;
  provinces: string[];
}

/**
 * customer-service renvoie l'institution telle quelle sur GET et PATCH
 * (aucun intercepteur d'enveloppe), mais certaines routes l'enveloppent dans
 * `{ success, data }`. On accepte les deux, et une réponse sans institution
 * est une erreur : la laisser passer vidait l'état du formulaire, qui restait
 * ensuite sur « Chargement du formulaire... ».
 */
const unwrapInstitution = (response: unknown): FinancialInstitutionResponse => {
  const raw = response as { data?: FinancialInstitutionResponse; id?: string } | null;
  const institution = raw?.data?.id ? raw.data : raw?.id ? (raw as FinancialInstitutionResponse) : null;
  if (!institution) {
    throw new Error('Réponse du serveur sans institution. Rechargez la page pour vérifier l\'enregistrement.');
  }
  return institution;
};

class FinancialInstitutionApiService {
  private baseUrl = '/financial-institutions';

  /**
   * Crée une nouvelle institution financière
   * @param data - TOUTES les données du formulaire FinancialInstitutionForm
   */
  async createInstitution(data: CreateFinancialInstitutionRequest): Promise<FinancialInstitutionResponse> {
    // ✨ Log explicite du userId AVANT sanitisation
    console.log('👤 userId reçu dans createInstitution:', data.userId);
    
    const sanitizedData = sanitizeInstitutionData(data, 'create');
    
    // ✨ Vérification que userId est toujours présent après sanitisation
    console.log('👤 userId après sanitisation:', sanitizedData.userId);
    
    if (!sanitizedData.userId) {
      console.error('❌ ERREUR: userId manquant après sanitisation!');
      throw new Error('userId est obligatoire pour créer une institution financière');
    }
    
    console.log('📤 FinancialInstitutionApiService.createInstitution - Envoi de TOUTES les données:', sanitizedData);
    const response = await api.post<ApiResponse<FinancialInstitutionResponse>>(this.baseUrl, sanitizedData);
    console.log('✅ FinancialInstitutionApiService.createInstitution - Réponse reçue:', response.data);
    return response.data;
  }

  /**
   * Récupère une institution financière par son ID
   * ✨ Transforme les données backend vers format frontend (mapping des champs)
   */
  async getInstitution(institutionId: string): Promise<FinancialInstitutionResponse> {
    console.log(`📥 FinancialInstitutionApiService.getInstitution - Récupération de l'institution ${institutionId}`);
    const response = await api.get<ApiResponse<FinancialInstitutionResponse>>(`${this.baseUrl}/${institutionId}`);
    
    // 🔍 Debug: Afficher la structure de la réponse
    console.log('📦 Réponse brute du backend:', response);
    // La réponse peut être enveloppée ou non : on la lit sans présumer de sa forme.
    const raw = response as unknown as { data?: FinancialInstitutionResponse; id?: string };
    console.log('📦 response.data:', raw.data);
    console.log('📦 response.id:', raw.id);
    
    // ✨ Gérer les deux formats possibles de réponse:
    // 1. { success: true, data: {...institution...} } → extraire response.data
    // 2. {...institution...} directement → utiliser response
    const institutionData: FinancialInstitutionResponse = raw.data && raw.data.id
      ? raw.data
      : (response as unknown as FinancialInstitutionResponse);
    
    console.log('📦 Données institution extraites:', institutionData);
    
    // ✨ Transformer les données backend → frontend (ex: autoriteSupervision → autoritéSupervision)
    const transformedData = transformBackendToFrontend(institutionData);
    console.log('✅ Données transformées pour le frontend:', transformedData);
    
    return transformedData;
  }

  /**
   * Met à jour une institution financière
   * @param data - TOUTES les données modifiées (partielles ou complètes)
   */
  async updateInstitution(institutionId: string, data: UpdateFinancialInstitutionRequest): Promise<FinancialInstitutionResponse> {
    const sanitizedData = sanitizeInstitutionData(data, 'update');
    console.log(`📤 FinancialInstitutionApiService.updateInstitution - Envoi de TOUTES les données pour ${institutionId}:`, sanitizedData);
    const response = await api.patch<unknown>(`${this.baseUrl}/${institutionId}`, sanitizedData);
    console.log('✅ FinancialInstitutionApiService.updateInstitution - Réponse reçue:', response);
    // Le PATCH renvoie l'institution nue : lire `response.data` donnait
    // undefined, et l'écran restait en chargement après chaque sauvegarde.
    return transformBackendToFrontend(unwrapInstitution(response));
  }

  /**
   * @deprecated Utiliser cloudinaryService.uploadLogo() puis updateInstitution({ logo: url }) à la place.
   * Cette méthode envoie le fichier directement au backend, ce qui n'est pas recommandé.
   * Le flux correct: Fichier → Cloudinary → URL → Backend
   */
  async uploadLogo(institutionId: string, logoFile: File): Promise<{ url: string }> {
    console.warn('⚠️ uploadLogo est déprécié. Utilisez cloudinaryService.uploadLogo() puis updateInstitution()');
    const response = await api.upload<ApiResponse<{ url: string }>>(
      `${this.baseUrl}/${institutionId}/logo`,
      logoFile,
      'logo'
    );
    return response.data;
  }

  /**
   * @deprecated Utiliser cloudinaryService.uploadAvatar() puis updateInstitution({ ceo: { ...ceo, photo: url }}) à la place.
   * Cette méthode envoie le fichier directement au backend, ce qui n'est pas recommandé.
   * Le flux correct: Fichier → Cloudinary → URL → Backend
   */
  async uploadCeoPhoto(institutionId: string, photoFile: File): Promise<{ url: string }> {
    console.warn('⚠️ uploadCeoPhoto est déprécié. Utilisez cloudinaryService.uploadAvatar() puis updateInstitution()');
    const response = await api.upload<ApiResponse<{ url: string }>>(
      `${this.baseUrl}/${institutionId}/ceo-photo`,
      photoFile,
      'photo'
    );
    return response.data;
  }

  // ── Agences ─────────────────────────────────────────────

  async listBranches(institutionId: string): Promise<InstitutionBranch[]> {
    const response = await api.get<ApiResponse<{ branches?: InstitutionBranch[] } | InstitutionBranch[]>>(
      `${this.baseUrl}/${institutionId}/branches`
    );
    const payload = response.data as { branches?: InstitutionBranch[] } | InstitutionBranch[];
    if (Array.isArray(payload)) return payload;
    return payload?.branches || [];
  }

  async addBranch(institutionId: string, branch: InstitutionBranchInput): Promise<InstitutionBranch> {
    const response = await api.post<ApiResponse<InstitutionBranch>>(
      `${this.baseUrl}/${institutionId}/branches`,
      branch
    );
    return response.data;
  }

  async updateBranch(
    institutionId: string,
    branchId: string,
    branch: Partial<InstitutionBranchInput>
  ): Promise<InstitutionBranch> {
    const response = await api.patch<ApiResponse<InstitutionBranch>>(
      `${this.baseUrl}/${institutionId}/branches/${branchId}`,
      branch
    );
    return response.data;
  }

  async removeBranch(institutionId: string, branchId: string): Promise<{ message: string }> {
    const response = await api.delete<ApiResponse<{ message: string }>>(
      `${this.baseUrl}/${institutionId}/branches/${branchId}`
    );
    return response.data;
  }

  async addTeamMember(institutionId: string, memberData: {
    name: string;
    position: string;
    bio?: string;
  }): Promise<{ id: string; message: string }> {
    const response = await api.post<ApiResponse<{ id: string; message: string }>>(
      `${this.baseUrl}/${institutionId}/team`,
      memberData
    );
    return response.data;
  }

  async removeTeamMember(institutionId: string, memberId: string): Promise<{ message: string }> {
    const response = await api.delete<ApiResponse<{ message: string }>>(
      `${this.baseUrl}/${institutionId}/team/${memberId}`
    );
    return response.data;
  }

  /**
   * @deprecated Utiliser cloudinaryService.uploadAvatar() puis utiliser l'URL lors de l'ajout du membre.
   * Cette méthode envoie le fichier directement au backend, ce qui n'est pas recommandé.
   * Le flux correct: Fichier → Cloudinary → URL → Backend
   */
  async uploadTeamMemberPhoto(institutionId: string, memberId: string, photoFile: File): Promise<{ url: string }> {
    console.warn('⚠️ uploadTeamMemberPhoto est déprécié. Utilisez cloudinaryService.uploadAvatar() à la place');
    const response = await api.upload<ApiResponse<{ url: string }>>(
      `${this.baseUrl}/${institutionId}/team/${memberId}/photo`,
      photoFile,
      'photo'
    );
    return response.data;
  }

  /**
   * Programmes d'appui validés (ONG, projets) qui suivent des AVEC, pour le
   * choix du programme sur le formulaire AVEC. Filtre par nom et province.
   */
  async listSupportPrograms(criteria: { search?: string; province?: string } = {}): Promise<SupportProgramOption[]> {
    const params: Record<string, string> = {};
    if (criteria.search?.trim()) params.search = criteria.search.trim();
    if (criteria.province?.trim()) params.province = criteria.province.trim();
    const body = await api.get<unknown>(`${this.baseUrl}/support-programs`, params);
    const data = (body as { data?: unknown })?.data ?? body;
    return Array.isArray(data) ? (data as SupportProgramOption[]) : [];
  }

  async listInstitutions(filters?: FinancialInstitutionFilters): Promise<{
    institutions: FinancialInstitutionResponse[];
    meta: { pagination: { page: number; limit: number; total: number; pages: number } };
  }> {
    const response = await api.get<ApiResponse<FinancialInstitutionResponse[]>>(this.baseUrl, filters);
    return {
      institutions: response.data,
      meta: response.meta?.pagination ? { pagination: response.meta.pagination } : { pagination: { page: 1, limit: 10, total: 0, pages: 0 } }
    };
  }

  /**
   * ✨ Récupère l'institution financière de l'utilisateur connecté
   * Utilise la liste filtrée par userId pour trouver l'institution
   * @param userId - L'ID de l'utilisateur connecté
   * @returns L'institution de l'utilisateur ou null si non trouvée
   */
  async getMyInstitution(userId: string): Promise<FinancialInstitutionResponse | null> {
    if (!userId) {
      console.warn('⚠️ getMyInstitution: userId manquant');
      return null;
    }
    
    console.log(`📥 FinancialInstitutionApiService.getMyInstitution - Recherche d'institution pour userId: ${userId}`);
    
    try {
      // Utiliser le endpoint de liste avec le userId comme filtre implicite
      // Le backend devrait retourner les institutions de l'utilisateur
      const response = await api.get<ApiResponse<FinancialInstitutionResponse[]>>(this.baseUrl);
      
      // 🔍 Debug: Afficher la structure complète de la réponse
      console.log('📦 Réponse brute getMyInstitution (complète):', JSON.stringify(response, null, 2));
      console.log('📦 response.data:', response.data);
      console.log('📦 Type de response.data:', typeof response.data, Array.isArray(response.data));
      
      // Le backend retourne { data: [...], meta: {...} }
      // response est déjà l'objet { data: [...], meta: {...} }
      const institutions = response.data || [];
      
      console.log('📦 Nombre d\'institutions trouvées:', institutions.length);
      console.log('📦 UserIds des institutions:', institutions.map((inst) => inst.userId));
      console.log('📦 UserId recherché:', userId);
      
      // Filtrer pour trouver l'institution de cet utilisateur
      const userInstitution = institutions.find((inst) => inst.userId === userId);
      
      if (userInstitution) {
        console.log('✅ Institution trouvée pour l\'utilisateur:', userInstitution.denominationSociale);
        console.log('📦 Données brutes de l\'institution:', JSON.stringify(userInstitution, null, 2));
        // Transformer les données backend → frontend
        const transformed = transformBackendToFrontend(userInstitution);
        console.log('📦 Données transformées:', JSON.stringify(transformed, null, 2));
        return transformed;
      }
      
      console.log('ℹ️ Aucune institution trouvée pour cet utilisateur');
      console.log('ℹ️ Comparaison userId:', { searchedUserId: userId, availableUserIds: institutions.map((inst) => inst.userId) });
      return null;
    } catch (error) {
      console.error('❌ Erreur lors de la recherche de l\'institution utilisateur:', error);
      return null;
    }
  }
}

export const financialInstitutionApiService = new FinancialInstitutionApiService();
export default financialInstitutionApiService;
